import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check, Search } from 'lucide-react';

interface DropdownFieldProps {
  label?: string;
  value: string;
  placeholder: string;
  options: string[];
  onSelect: (value: string) => void;
  title?: string;
  disabled?: boolean;
  compact?: boolean;
}

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  color: 'var(--tabbio-text-tertiary)',
  marginBottom: 6,
  display: 'block',
};

const TRIGGER_STYLE_BASE: React.CSSProperties = {
  width: '100%',
  borderRadius: 9999,
  border: '1px solid var(--tabbio-border)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  color: 'var(--tabbio-text)',
  backgroundColor: 'var(--tabbio-bg)',
  fontFamily: 'inherit',
  transition: 'border-color 150ms, background-color 150ms, opacity 150ms',
  boxSizing: 'border-box',
  cursor: 'pointer',
  textAlign: 'start',
};

const TRIGGER_STYLE_DEFAULT: React.CSSProperties = {
  ...TRIGGER_STYLE_BASE,
  minHeight: 50,
  padding: '14px 20px',
  fontSize: 16,
};

const TRIGGER_STYLE_COMPACT: React.CSSProperties = {
  ...TRIGGER_STYLE_BASE,
  minHeight: 40,
  padding: '8px 14px',
  fontSize: 14,
};

const PANEL_STYLE: React.CSSProperties = {
  position: 'fixed',
  // The panel is portalled to <body>, so this z-index actually wins. Rendered
  // in place it could not: every react-native-web View is `position: relative;
  // z-index: 0`, so an ancestor View traps the panel in its own stacking
  // context and later sibling Views paint over it however high it asks to be.
  zIndex: 10000,
  borderRadius: 14,
  border: '1px solid var(--tabbio-border-subtle)',
  backgroundColor: 'var(--tabbio-bg)',
  boxShadow: '0 10px 28px rgba(0, 0, 0, 0.16), 0 2px 8px rgba(0, 0, 0, 0.12)',
  overflow: 'hidden',
};

/** Gap between trigger and panel, and between panel and viewport edge. */
const PANEL_GAP = 8;
const VIEWPORT_MARGIN = 8;
const PANEL_MAX_HEIGHT = 300;
const PANEL_MIN_HEIGHT = 120;

/**
 * Above this many options the panel grows a search box. Below it, scanning the
 * list is faster than typing, and a filter would just add a step.
 */
const SEARCH_THRESHOLD = 8;

interface PanelPosition {
  left: number;
  width: number;
  maxHeight: number;
  /** Exactly one is set: `top` opens the panel downwards, `bottom` upwards. */
  top?: number;
  bottom?: number;
}

/**
 * Nearest ancestor that would have clipped the panel before it was portalled —
 * the wizard's scroll view, the dialog card, etc. Used to hide the panel once
 * the trigger scrolls out of it, instead of letting a `fixed` panel float on
 * over the rest of the page.
 */
function findClippingAncestor(node: HTMLElement | null): HTMLElement | null {
  let current = node?.parentElement ?? null;
  while (current && current !== document.body) {
    const { overflow, overflowX, overflowY } = window.getComputedStyle(current);
    if (/auto|scroll|hidden/.test(`${overflow} ${overflowX} ${overflowY}`)) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

/** The one rule the search box applies, shared by the filter and the cursor. */
function matchesQuery(option: string, trimmedLowerQuery: string) {
  return option.toLowerCase().includes(trimmedLowerQuery);
}

function getPanelTitle(label: string | undefined, title: string | undefined, fallback: string) {
  if (title?.trim()) return title.trim();
  if (label?.trim()) return label.replace('*', '').trim();
  return fallback;
}

export function DropdownField({
  label,
  value,
  placeholder,
  options,
  onSelect,
  title,
  disabled = false,
  compact = false,
}: DropdownFieldProps) {
  const { t } = useTranslation('common');
  const triggerStyle = compact ? TRIGGER_STYLE_COMPACT : TRIGGER_STYLE_DEFAULT;
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<PanelPosition | null>(null);
  const [query, setQuery] = useState('');
  // The keyboard/pointer cursor. Hover and arrow keys drive the same highlight,
  // so a mouse never leaves a second, competing one behind.
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const clipRef = useRef<HTMLElement | null>(null);
  const panelId = useId();
  // Portal target: only available after mount on the client (SSR-safe).
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);

  const panelTitle = useMemo(
    () => getPanelTitle(label, title, t('dropdown.title')),
    [label, t, title],
  );

  const searchable = options.length > SEARCH_THRESHOLD;

  // Read at open time only. Callers build `options` inline (FlowDropdown maps
  // its items to labels on every render), so depending on the array itself
  // would re-run the open effect on each keystroke and wipe the search box.
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const valueRef = useRef(value);
  valueRef.current = value;

  const visibleOptions = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!searchable || !trimmed) return options;
    return options.filter((option) => matchesQuery(option, trimmed));
  }, [options, query, searchable]);

  /**
   * Whether the listbox is actually in the DOM. `open` is not enough:
   * `measure()` clears `position` when the trigger scrolls out of its clipping
   * ancestor, which unmounts the panel while `open` stays true. Every ARIA
   * reference to the listbox — and the key handler — follows this, not `open`.
   */
  const panelMounted = open && position !== null;

  /**
   * The option `aria-activedescendant` may name. Only ever an id that is
   * actually rendered: an empty filter result has no active option at all, a
   * closed or unmounted panel has none either, and naming a missing id is both
   * an `aria-valid-attr-value` violation and a screen reader announcing
   * nothing.
   */
  const activeOptionId =
    panelMounted && activeIndex >= 0 && activeIndex < visibleOptions.length
      ? `${panelId}-option-${activeIndex}`
      : undefined;

  useEffect(() => {
    if (typeof document !== 'undefined') {
      setPortalTarget(document.body);
    }
  }, []);

  /**
   * Every close path routes through here. The panel is portalled, so when it
   * unmounts the focus it was holding (the search input) falls to <body> —
   * outside the `aria-modal` dialog the trigger usually sits in, which strands
   * the keyboard. Handing focus back to the trigger keeps the dialog's own tab
   * order intact.
   */
  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    // The options unmount with the panel, so the cursor has to go with them:
    // a surviving `activeIndex` left the closed trigger's
    // `aria-activedescendant` naming a node that no longer exists.
    setActiveIndex(-1);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    // The trigger button is the container's last child and spans its full
    // width, so the container rect gives the panel its edge and width.
    const rect = container.getBoundingClientRect();
    const clip = clipRef.current?.getBoundingClientRect();
    if (clip && (rect.bottom <= clip.top || rect.top >= clip.bottom)) {
      // The field has scrolled entirely out of its container: hide rather than
      // trail a fixed panel across whatever is outside it. Close instead of
      // only nulling the position — unmounting the portalled panel while it
      // holds focus drops focus on <body>, outside the React tree that owns
      // both this dropdown's key handler and the surrounding dialog's, so
      // Escape and Tab reach nothing and the keyboard is trapped (WCAG 2.1.2).
      // Closing also stops `open` sitting true with nothing rendered.
      //
      // Only reclaim focus when the panel is what holds it: a scroll driven
      // from elsewhere on the page must not yank focus out of whatever the
      // user is actually using. The test above needs the trigger to be fully
      // past an edge of the clip, so an ordinary scroll with the panel still
      // correctly positioned never reaches here.
      const focusInPanel = Boolean(
        document.activeElement &&
          panelRef.current?.contains(document.activeElement),
      );
      setPosition(null);
      close(focusInPanel);
      return;
    }

    const viewportHeight = window.innerHeight;
    const spaceBelow =
      viewportHeight - rect.bottom - PANEL_GAP - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - PANEL_GAP - VIEWPORT_MARGIN;
    // Prefer opening down; flip up only when down is cramped and up is roomier.
    const openUp = spaceBelow < PANEL_MIN_HEIGHT && spaceAbove > spaceBelow;
    const available = Math.max(0, openUp ? spaceAbove : spaceBelow);
    // Clamp strictly to the room on the chosen side — a minimum height here
    // would push the panel off the edge it was measured against.
    const maxHeight = Math.min(PANEL_MAX_HEIGHT, available);

    setPosition({
      left: rect.left,
      width: rect.width,
      maxHeight,
      ...(openUp
        ? { bottom: viewportHeight - rect.top + PANEL_GAP }
        : { top: rect.bottom + PANEL_GAP }),
    });
  }, [close]);

  // Opening starts on the current selection, so Enter re-picks it and the arrow
  // keys step away from where the user already is.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(optionsRef.current.indexOf(valueRef.current));
    if (searchable) {
      // The panel portals on the same tick; focus after it exists.
      const id = window.requestAnimationFrame(() => searchRef.current?.focus());
      return () => window.cancelAnimationFrame(id);
    }
    // Without a search box the trigger keeps focus — and it has to actually
    // hold it, because the key handler now lives on this React subtree and
    // Safari does not focus a <button> on click.
    triggerRef.current?.focus();
  }, [open, searchable]);

  // Keep the highlighted row in view whether it was reached by keyboard or by
  // reopening on a selection far down a long list.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    listRef.current
      ?.querySelector(`[data-option-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  useEffect(() => {
    if (!open) {
      setPosition(null);
      clipRef.current = null;
      return;
    }
    clipRef.current = findClippingAncestor(containerRef.current);
    measure();
    // Capture phase: scroll events don't bubble, so this is how a scroll of the
    // dialog body (or any ancestor) keeps the fixed panel pinned to its trigger.
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [measure, open]);

  const handleSelect = useCallback(
    (option: string) => {
      onSelect(option);
      close();
    },
    [close, onSelect],
  );

  /**
   * Keys are handled on this component's React subtree, not on `window`.
   * The panel is a portal, so a keypress inside it still bubbles through the
   * React tree to this container — and to whatever wraps the field. `WebDialog`
   * closes itself on a bubbled Escape, so an Escape aimed at an open dropdown
   * used to close the dropdown AND step the job wizard back a page;
   * `preventDefault` alone never stopped that. Handling the key here lets us
   * stop propagation for exactly the keys the open panel owns.
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Gated on the panel, not on `open`: an out-of-view dropdown has no
      // panel to drive, and swallowing Escape there ate the dialog's own
      // Escape — the first press did nothing and the second closed the wizard.
      if (!panelMounted) return;
      const count = visibleOptions.length;
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          event.stopPropagation();
          close();
          return;
        case 'ArrowDown':
        case 'ArrowUp': {
          if (count === 0) return;
          event.preventDefault();
          event.stopPropagation();
          const step = event.key === 'ArrowDown' ? 1 : -1;
          setActiveIndex((prev) => {
            // From "nothing highlighted", Down enters at the top and Up at the
            // bottom; from anywhere else the ends stop rather than wrap, so
            // holding a key cannot silently loop past the choice you wanted.
            if (prev < 0) return step === 1 ? 0 : count - 1;
            return Math.min(count - 1, Math.max(0, prev + step));
          });
          return;
        }
        case 'Home':
          if (count === 0) return;
          event.preventDefault();
          event.stopPropagation();
          setActiveIndex(0);
          return;
        case 'End':
          if (count === 0) return;
          event.preventDefault();
          event.stopPropagation();
          setActiveIndex(count - 1);
          return;
        case 'Enter': {
          const option = visibleOptions[activeIndex];
          if (!option) return;
          event.preventDefault();
          event.stopPropagation();
          handleSelect(option);
          return;
        }
        case 'Tab': {
          // Tabbing away commits nothing and closes, matching a native select.
          // Only swallow the tab when the portalled panel is what holds focus
          // (the search box): a native tab from there lands on whatever
          // follows the portal in <body>, outside the dialog entirely, so
          // focus goes back to the trigger instead. With no search box the
          // trigger already has focus and is already in the dialog's tab
          // order, so let the browser move to the next control — swallowing it
          // there cost a keyboard user a whole press.
          const focusInPanel = Boolean(
            document.activeElement &&
              panelRef.current?.contains(document.activeElement),
          );
          if (!focusInPanel) {
            close(false);
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          close();
          return;
        }
        default:
      }
    },
    [activeIndex, close, handleSelect, panelMounted, visibleOptions],
  );

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (containerRef.current?.contains(target)) return;
      // The panel is portalled, so it is not inside `containerRef`. Without
      // this an option press would unmount the panel on mousedown and the
      // click would never land.
      if (panelRef.current?.contains(target)) return;
      // Only reclaim focus when the panel is the thing holding it: this runs on
      // mousedown, before the browser focuses whatever was clicked, so the
      // click still wins — it just no longer leaves focus on <body> when the
      // press landed on something unfocusable.
      const focusInPanel = Boolean(
        document.activeElement &&
          panelRef.current?.contains(document.activeElement),
      );
      close(focusInPanel);
    };
    window.addEventListener('mousedown', handlePointerDown);
    return () => window.removeEventListener('mousedown', handlePointerDown);
  }, [close, open]);


  const panel =
    open && position && portalTarget
      ? createPortal(
          <div
            ref={panelRef}
            style={{
              ...PANEL_STYLE,
              left: position.left,
              width: position.width,
              maxHeight: position.maxHeight,
              ...(position.top !== undefined ? { top: position.top } : null),
              ...(position.bottom !== undefined
                ? { bottom: position.bottom }
                : null),
            }}
            id={panelId}
            aria-label={panelTitle}
          >
            {searchable ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 14px',
                  borderBottom: '1px solid var(--tabbio-border-subtle)',
                }}
              >
                <Search size={15} color="var(--tabbio-text-tertiary)" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(event) => {
                    const next = event.target.value;
                    setQuery(next);
                    // A new result set makes the old index meaningless; land on
                    // the first match so Enter picks the obvious one — and on
                    // no option at all when nothing matches.
                    const trimmed = next.trim().toLowerCase();
                    const hasMatch =
                      !trimmed ||
                      optionsRef.current.some((option) =>
                        matchesQuery(option, trimmed),
                      );
                    setActiveIndex(hasMatch ? 0 : -1);
                  }}
                  placeholder={t('dropdown.searchPlaceholder')}
                  aria-label={t('dropdown.searchPlaceholder')}
                  // The search box is the focused element, so the combobox
                  // wiring — and `aria-activedescendant` above all — has to sit
                  // here: a screen reader only announces the active option from
                  // the element that actually has focus.
                  role="combobox"
                  aria-expanded
                  aria-autocomplete="list"
                  autoComplete="off"
                  aria-controls={`${panelId}-list`}
                  aria-activedescendant={activeOptionId}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    color: 'var(--tabbio-text)',
                    fontFamily: 'inherit',
                    fontSize: 14,
                  }}
                />
              </div>
            ) : null}
            <div
              ref={listRef}
              id={`${panelId}-list`}
              role="listbox"
              aria-label={panelTitle}
              style={{ overflowY: 'auto', maxHeight: position.maxHeight }}
            >
              {visibleOptions.length === 0 ? (
                <div
                  style={{
                    padding: '12px 14px',
                    fontSize: 14,
                    color: 'var(--tabbio-text-tertiary)',
                  }}
                >
                  {options.length === 0
                    ? t('dropdown.empty')
                    : t('dropdown.noResults')}
                </div>
              ) : visibleOptions.map((option, index) => {
                const isSelected = option === value;
                const isActive = index === activeIndex;
                return (
                  <button
                    key={option}
                    id={`${panelId}-option-${index}`}
                    data-option-index={index}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={-1}
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => setActiveIndex(index)}
                    style={{
                      width: '100%',
                      border: 'none',
                      borderBottom: '1px solid var(--tabbio-border-subtle)',
                      background: isActive
                        ? 'var(--tabbio-surface)'
                        : isSelected
                          ? 'color-mix(in srgb, var(--tabbio-surface) 72%, var(--tabbio-bg))'
                          : 'transparent',
                      padding: '11px 14px',
                      fontFamily: 'inherit',
                      fontSize: 14,
                      color: 'var(--tabbio-text)',
                      cursor: 'pointer',
                      textAlign: 'start',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 10,
                    }}
                  >
                    <span style={{ fontWeight: isSelected ? 600 : 400 }}>{option}</span>
                    {isSelected ? <Check size={16} color="var(--tabbio-button-primary-bg)" /> : null}
                  </button>
                );
              })}
            </div>
          </div>,
          portalTarget,
        )
      : null;

  // The portalled panel is a React child of this container, so its keypresses
  // bubble here through the React tree: one handler covers focus on the trigger
  // and focus in the search box alike.
  return (
    <div ref={containerRef} style={{ position: 'relative' }} onKeyDown={handleKeyDown}>
      {label ? <label style={LABEL_STYLE}>{label}</label> : null}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (disabled) return;
          if (open) {
            close();
            return;
          }
          setOpen(true);
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        // `panelMounted`, not `open`, so all three ARIA references to the
        // listbox move together: a combobox that says `aria-expanded="true"`
        // with no `aria-controls` is an axe `aria-required-attr` violation.
        aria-expanded={panelMounted}
        // The listbox, not the panel wrapper: the wrapper has no role, and
        // `aria-activedescendant` on a combobox is only meaningful against the
        // listbox that combobox controls. The search input already does this.
        aria-controls={panelMounted ? `${panelId}-list` : undefined}
        // Without a search box the trigger keeps focus, so it is the element
        // that has to name the active option — and only a combobox may
        // (ARIA 1.2's select-only pattern). With a search box the input is the
        // combobox instead and this stays a plain disclosure button.
        role={searchable ? undefined : 'combobox'}
        aria-activedescendant={searchable ? undefined : activeOptionId}
        style={{
          ...triggerStyle,
          opacity: disabled ? 0.5 : 1,
          color: value ? 'var(--tabbio-text)' : 'var(--tabbio-text-tertiary)',
          cursor: disabled ? 'default' : 'pointer',
          borderColor: open ? 'var(--tabbio-text-secondary)' : 'var(--tabbio-border)',
        }}
      >
        <span
          style={{
            flex: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {value || placeholder}
        </span>
        <ChevronDown
          size={14}
          color="var(--tabbio-text-tertiary)"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 140ms' }}
        />
      </button>

      {panel}
    </div>
  );
}
